/* global globalThis */
import { useEffect } from "react";
import axiosClient from "../../api-client/AxiosClient";
const application_id = process.env.REACT_APP_APPLICATION_ID_OWL;

export const getUserSelectedOrgConfig = async () => {
    try {
        const response = await axiosClient.get(
            "/api/Organization/GetUserSelectedOrgConfig"
        );
        return response?.data ?? null;
    } catch (error) {
        console.error("Failed to get user selected org config:", error);
        return null;
    }
};

export const setUserSelectedOrgConfig = async () => {
    try {
        const organizationConfigId = localStorage.getItem("organization_config_id");
        const selectedCorporation = localStorage.getItem("selectedCorporation");
        const UserMappingFileId = localStorage.getItem("UserMappingFileId");
        const organizationId = selectedCorporation
            ? JSON.parse(selectedCorporation)?.value?.toString()
            : null;

        if (!organizationConfigId || !organizationId) {
            return null;
        }

        const payload = {
            organizationConfigId: String(organizationConfigId),
            organizationId: String(organizationId),
            userMappingFileId: String(UserMappingFileId),
        };

        const response = await axiosClient.post(
            "/api/Organization/SetUserSelectedOrgConfig",
            payload
        );

        return response?.data ?? null;
    } catch (error) {
        console.error("Failed to set user selected org config:", error);
        return null;
    }
};



/**
 * Merges roles and permissions from the API response into a single permissions object.
 * Maps specific Owl Sight modules with fallbacks to generic keys, and filters by 'canView' access.
 * 
 * @param {Object} rolesData - The raw permissions data from the API
 * @returns {Object} Merged and filtered permissions
 */
const mergeRolesAndPermissions = (rolesData) => {
    const merged = {};
    // Map of [app-specific-key]: [generic-api-fallback-key]
    const mapping = {
        OSDashboard: "Dashboard",
        OSDataSource: "DataSource",
        DQ: "DQ",
        IssuePredicit: "IssuePredicit",
        PII: "PII",
        AIAsst: "AIAsst"
    };

    if (!rolesData || typeof rolesData !== "object") return merged;

    // The API returns roles containing modules (e.g., { "Admin": { "Dashboard": {...} } })
    Object.values(rolesData).forEach((role) => {
        if (!role || typeof role !== "object") return;

        Object.entries(mapping).forEach(([appKey, apiKey]) => {
            // Check product-specific key first, then fallback to generic key
            const permissions = role[appKey] || role[apiKey];

            // Only include module if it has canView permission
            if (permissions && typeof permissions === "object" && permissions.canView) {
                if (!merged[appKey]) {
                    merged[appKey] = { ...permissions };
                } else {
                    // Merge existing permissions with current role (OR logic for booleans)
                    Object.entries(permissions).forEach(([key, value]) => {
                        if (typeof value === "boolean") {
                            merged[appKey][key] = merged[appKey][key] || value;
                        }
                    });
                }
            }
        });
    });

    return merged;
};

export const fetchPermissions = async (configId) => {
    try {
        if (!configId) {
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
            return null;
        }

        const response = await axiosClient.get(`/api/User/Permissions?OrgConfigId=${configId}&ApplicationId=${application_id}`);

        const permissions = response?.data ? mergeRolesAndPermissions(response.data) : {};

        // Always set/update session storage to ensure stale permissions are cleared if access is revoked
        sessionStorage.setItem("rolesAndPermission", JSON.stringify(permissions));

        // Dispatch event to notify listeners (like layout.js) that permissions are loaded/ready
        globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: permissions }));
        return permissions;
    } catch (error) {
        console.error("Failed to fetch permissions:", error);
        // Dispatch null to prevent infinite loading state
        globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
        return null;
    }
};

export const useOrgConfigSync = () => {
    useEffect(() => {
        // Initial check on mount to ensure layout.js unblocks
        const storedRoles = sessionStorage.getItem("rolesAndPermission");
        const configId = localStorage.getItem("organization_config_id");

        if (storedRoles) {
            // Already have everything, just ensure Layout is aware if it missed the initial state
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: JSON.parse(storedRoles) }));
        } else if (configId) {
            // We have a config but no roles - fetch them
            fetchPermissions(configId);
        } else {
            // NO config and NO roles - unblock UI so user can select from Header
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
        }

        const handleStorageChange = async (e) => {
            if (
                e.detail?.key === "organization_config_id" ||
                e.detail?.key === "selectedCorporation"
            ) {
                await setUserSelectedOrgConfig();

                // Fetch permissions if config changed
                if (e.detail?.key === "organization_config_id" && e.detail?.newValue) {
                    await fetchPermissions(e.detail.newValue);
                }
            }
        };

        globalThis.addEventListener("customStorageChange", handleStorageChange);
        return () => {
            globalThis.removeEventListener("customStorageChange", handleStorageChange);
        };
    }, []);
};